import React, { useState } from "react";
import { ScanLine, RefreshCw, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Card } from "../UI";

const Kpi = ({ label, value, sub, accent, testid }) => (
  <div className="rounded-lg border border-white/10 px-3 py-2" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`font-display text-xl font-bold ${accent ? "text-amber-300" : ""}`}>{value}</div>
    {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
  </div>
);

const COLS = [["visits", "Scans"], ["unique_visitors", "Visitors"], ["races_viewed", "Races"], ["reports", "Reports"], ["demo_sessions", "Demos"]];

const MiniTable = ({ rows, nameKey, subKey, testid, empty }) => (
  !rows?.length ? <div className="text-xs text-slate-500 py-3">{empty}</div> : (
    <table className="w-full text-xs" data-testid={testid}>
      <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500"><th className="text-left py-1.5 font-medium">Name</th>{COLS.map(([k, l]) => <th key={k} className="text-right py-1.5 px-2 font-medium">{l}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => (
        <tr key={i} className="border-t border-white/5" data-testid={`${testid}-row-${i}`}>
          <td className="py-1.5"><div className="text-slate-200">{r[nameKey] || "—"}</div>{subKey && r[subKey] && <div className="text-[10px] text-slate-500">{r[subKey]}</div>}</td>
          {COLS.map(([k]) => <td key={k} className={`text-right px-2 tabular-nums ${k === "demo_sessions" ? "text-amber-300 font-medium" : "text-slate-300"}`}>{r[k] || 0}</td>)}
        </tr>))}</tbody>
    </table>
  )
);

const FU_LABEL = { start_sms: "SMS", report_sms: "SMS", start_email: "Email", session_end_email: "End-of-session", eod_email: "6pm recap" };
const FU_CLS = { sent: "text-emerald-300 border-emerald-400/30", pending: "text-slate-400 border-white/10", skipped: "text-slate-500 border-white/10 line-through", failed: "text-rose-300 border-rose-400/30" };
const Followups = ({ f }) => !f || !Object.keys(f).length ? null : (
  <div className="flex flex-wrap gap-1 mt-1" data-testid="fyr-lead-followups">
    {Object.entries(f).map(([k, s]) => <span key={k} className={`text-[9px] px-1.5 py-0.5 rounded border ${FU_CLS[s] || FU_CLS.pending}`} title={`${FU_LABEL[k] || k}: ${s}`}>{FU_LABEL[k] || k}{s === "pending" ? " ⏱" : s === "sent" ? " ✓" : ""}</span>)}
  </div>
);

const RESEND_KINDS = { guest: ["start_sms", "start_email", "session_end_email", "eod_email"], report: ["report_sms"] };

const LeadActions = ({ lead, onDone }) => {
  const [kind, setKind] = useState(lead.kind === "guest" ? "start_email" : "report_sms");
  const [busy, setBusy] = useState(false);
  const pending = Object.values(lead.followups || {}).filter((st) => st === "pending").length;
  const resend = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/promotions/followups/leads/${lead.id}/resend`, { kind }); toast[data.status === "sent" ? "success" : "error"](`${FU_LABEL[kind]}: ${data.status}${data.reason ? ` — ${data.reason}` : ""}`); onDone?.(); }
    catch (e) { toast.error(errMsg(e, "Resend failed")); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/promotions/followups/leads/${lead.id}/cancel`); toast.success(`Cancelled ${data.cancelled} queued message(s)`); onDone?.(); }
    catch (e) { toast.error(errMsg(e, "Cancel failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1 shrink-0" data-testid={`fyr-lead-actions-${lead.id}`}>
      <select value={kind} onChange={(e) => setKind(e.target.value)} className="bg-slate-900/80 border border-white/10 rounded px-1.5 py-1 text-[10px] text-slate-300" data-testid={`fyr-resend-kind-${lead.id}`}>
        {RESEND_KINDS[lead.kind].map((k) => <option key={k} value={k}>{FU_LABEL[k]}</option>)}
      </select>
      <button onClick={resend} disabled={busy} title="Resend now" className="p-1 rounded text-amber-300 hover:bg-white/5 disabled:opacity-40" data-testid={`fyr-resend-btn-${lead.id}`}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}</button>
      {pending > 0 && <button onClick={cancel} disabled={busy} title={`Cancel ${pending} queued`} className="p-1 rounded text-rose-300 hover:bg-white/5 disabled:opacity-40" data-testid={`fyr-cancel-btn-${lead.id}`}><XCircle className="w-3.5 h-3.5" /></button>}
    </div>
  );
};

export const FindYourRacePanel = ({ data, reload }) => {
  if (!data) return null;
  const fu = data.followups || {};
  return (
    <Card className="!p-5 space-y-4" data-testid="find-your-race-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="font-display font-bold text-sm inline-flex items-center gap-2"><ScanLine className="w-4 h-4 text-amber-300" /> Scan · Find Your Race</div>
        <div className="text-[11px] text-slate-500">QR scans → races explored → race reports & no-login demo sessions</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Kpi label="QR scans" value={data.visits} sub={`${data.unique_visitors} unique visitors`} testid="fyr-kpi-visits" />
        <Kpi label="Races explored" value={data.races_viewed} testid="fyr-kpi-races" />
        <Kpi label="Race reports sent" value={data.reports} sub="→ Access Requests" testid="fyr-kpi-reports" />
        <Kpi label="Demo sessions" value={data.demo_sessions} sub="30-min guest sandboxes" accent testid="fyr-kpi-demos" />
        <Kpi label="Conversion" value={data.visits ? `${Math.round(100 * (data.reports + data.demo_sessions) / data.visits)}%` : "—"} sub="leads per scan" testid="fyr-kpi-conv" />
        <Kpi label="Follow-ups" value={`${(fu.sms_sent || 0) + (fu.emails_sent || 0)}`} sub={`${fu.sms_sent || 0} SMS · ${fu.emails_sent || 0} email · ${fu.scheduled || 0} queued${fu.failed ? ` · ${fu.failed} failed` : ""}`} testid="fyr-kpi-followups" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div><div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">By trade show</div><MiniTable rows={data.by_event} nameKey="name" testid="fyr-by-event" empty="No tracked scans yet — print Find-Your-Race cards from a trade show." /></div>
        <div><div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">By booth staff</div><MiniTable rows={data.by_staff} nameKey="staff_name" subKey="event_name" testid="fyr-by-staff" empty="Attribute cards to a staffer to see who drives the most demos." /></div>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Latest leads</div>
        {!data.recent_leads?.length ? <div className="text-xs text-slate-500 py-2">No leads yet.</div> : (
          <div className="divide-y divide-white/5" data-testid="fyr-leads">
            {data.recent_leads.map((l) => (
              <div key={l.id} className="py-2 flex items-center gap-3 text-xs" data-testid={`fyr-lead-${l.id}`}>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${l.kind === "guest" ? "border-amber-400/40 text-amber-300" : "border-sky-400/40 text-sky-300"}`}>{l.kind === "guest" ? "Demo" : "Report"}</span>
                <div className="min-w-0 flex-1"><span className="text-slate-200 font-medium">{l.name}</span> <span className="text-slate-500">· {l.phone || l.email || "—"}{l.phone && l.email ? ` · ${l.email}` : ""}</span><div className="text-[10px] text-slate-500 truncate">{l.race_label}{l.event_name ? ` · ${l.event_name}` : ""}{l.staff_name ? ` · ${l.staff_name}` : ""}{l.resumes ? ` · reopened ×${l.resumes}` : ""}</div><Followups f={l.followups} /></div>
                <span className="text-[10px] text-slate-500 whitespace-nowrap">{l.created_at?.slice(0, 16).replace("T", " ")}</span>
                <LeadActions lead={l} onDone={reload} />
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};
